import { useCallback, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import type { Me } from "../api";
import { useT } from "../i18n";
import { useSignOut } from "../session";
import { QuoteIcon, SettingsIcon, ShieldIcon, SignOutIcon, UsageIcon } from "./icons";
import MutationError from "./MutationError";
import { useDismiss } from "./useDismiss";

/**
 * AccountMenu is the signed-in corner of the header: an avatar that opens a
 * small menu with who you are, your quotes, settings, your usage, the admin
 * panel for platform admins, and sign out.
 *
 * Top right rather than a sidebar, because the header is already the app's only
 * bar and a sidebar would take width a phone does not have. Sign out sits inside
 * the menu, not beside the avatar: it is the least frequent action on screen and
 * the easiest to hit by accident.
 *
 * On a phone the menu is a sheet from the bottom, where a thumb is; from the
 * small breakpoint up it is a dropdown under the avatar.
 */
export default function AccountMenu({ me }: { me: Me }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const sheet = useRef<HTMLDivElement>(null);
  const phone = usePhone();
  const close = useCallback(() => setOpen(false), []);
  const signOut = useSignOut();
  const t = useT();
  const name = me.user.name || me.user.email;

  // A tap outside or Escape, the two ways people expect to close a menu
  // without choosing anything.
  const within = useMemo(() => [root, sheet], []);
  useDismiss(open, within, close);

  const item =
    "flex w-full items-center gap-3 rounded-control px-3 py-3 text-start text-[0.95rem] text-ink transition-colors hover:bg-sunk disabled:opacity-50 sm:py-2 sm:text-sm";
  const icon = "size-5 text-muted sm:size-4";

  const panel = (
    <div
      role="menu"
      data-testid="account-menu-panel"
      className={
        phone
          ? "fixed inset-x-0 bottom-0 z-40 rounded-t-[1.5rem] bg-surface px-2 pt-2 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-[0_-12px_40px_-12px_rgb(0_0_0/0.35)]"
          : "absolute end-0 top-full z-30 mt-2 w-64 rounded-card bg-surface p-1.5 shadow-[0_16px_40px_-16px_rgb(0_0_0/0.35)] ring-1 ring-line"
      }
    >
      {phone && (
        <div aria-hidden="true" className="mx-auto mb-2 h-1 w-10 rounded-full bg-line-strong" />
      )}
      <div className="flex items-center gap-3 px-3 py-3 sm:py-2">
        <Avatar name={name} imageURL={me.user.image_url} size={40} />
        <div className="min-w-0">
          {me.user.name && (
            <p dir="auto" className="truncate text-sm font-medium">
              {me.user.name}
            </p>
          )}
          <p data-testid="account-email" className="truncate text-xs text-muted">
            {me.user.email}
          </p>
        </div>
      </div>
      <div className="mt-1 border-t border-line pt-1">
        <Link
          role="menuitem"
          to="/quotes"
          data-testid="menu-quotes"
          onClick={() => setOpen(false)}
          className={item}
        >
          <QuoteIcon className={icon} />
          {t.nav.myQuotes}
        </Link>
        <Link
          role="menuitem"
          to="/settings"
          data-testid="menu-settings"
          onClick={() => setOpen(false)}
          className={item}
        >
          <SettingsIcon className={icon} />
          {t.nav.settings}
        </Link>
        <Link
          role="menuitem"
          to="/usage"
          data-testid="menu-usage"
          onClick={() => setOpen(false)}
          className={item}
        >
          <UsageIcon className={icon} />
          {t.nav.usage}
        </Link>
        {/* Offered only to a platform admin. The server refuses everyone else
                regardless; this only keeps a link off screens it would 403. */}
        {me.user.platform_admin && (
          <Link
            role="menuitem"
            to="/admin"
            data-testid="menu-admin"
            onClick={() => setOpen(false)}
            className={item}
          >
            <ShieldIcon className={icon} />
            {t.nav.admin}
          </Link>
        )}
        <button
          role="menuitem"
          type="button"
          data-testid="sign-out"
          // The menu closes when the session ends, not before: closed
          // first, a refusal had nowhere to be said.
          onClick={() => signOut.mutate(undefined, { onSuccess: close })}
          disabled={signOut.isPending}
          className={item}
        >
          <SignOutIcon className={icon} />
          {t.nav.signOut}
        </button>
        <MutationError
          error={signOut.error}
          fallback={t.nav.signOutFailed}
          testId="sign-out-error"
          className="px-4 py-2"
        />
      </div>
    </div>
  );

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        data-testid="account-menu"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t.nav.accountMenu(name)}
        onClick={() => setOpen((was) => !was)}
        className="grid size-9 place-items-center rounded-full outline-none ring-offset-2 ring-offset-ground transition-shadow hover:ring-2 hover:ring-line-strong focus-visible:ring-2 focus-visible:ring-sage"
      >
        <Avatar name={name} imageURL={me.user.image_url} size={32} />
      </button>

      {open &&
        (phone
          ? // Portalled: the header's backdrop blur makes it the box a fixed
            // descendant is placed in, which put the sheet at the top.
            createPortal(
              <>
                <div
                  aria-hidden="true"
                  onClick={close}
                  className="fixed inset-0 z-30 bg-black/35 backdrop-blur-[2px]"
                />
                <div ref={sheet}>{panel}</div>
              </>,
              document.body,
            )
          : panel)}
    </div>
  );
}

/** usePhone is whether the viewport is below the small breakpoint. */
function usePhone(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const query = window.matchMedia("(max-width: 639.98px)");
      query.addEventListener("change", onChange);
      return () => query.removeEventListener("change", onChange);
    },
    () => window.matchMedia("(max-width: 639.98px)").matches,
    () => false,
  );
}

/**
 * Avatar is the Google profile picture, or initials when there is none or it
 * fails to load — a broken-image icon in the corner reads as a broken app.
 */
export function Avatar({
  name,
  imageURL,
  size,
}: {
  name: string;
  imageURL?: string;
  size: number;
}) {
  const [failed, setFailed] = useState(false);
  const box = { width: size, height: size };

  if (imageURL && !failed) {
    return (
      <img
        src={imageURL}
        alt=""
        style={box}
        // Google's avatar host refuses some requests that carry a referrer.
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
        className="shrink-0 rounded-full object-cover"
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      style={{ ...box, fontSize: Math.round(size * 0.4) }}
      className="grid shrink-0 place-items-center rounded-full bg-sage-soft font-medium text-sage-deep"
    >
      {initialsOf(name)}
    </span>
  );
}

/** initialsOf takes up to two initials from a name or an email's local part. */
export function initialsOf(name: string): string {
  const words = name
    .replace(/@.*$/, "")
    .split(/[\s._-]+/)
    .filter(Boolean);
  const letters = words.slice(0, 2).map((w) => Array.from(w)[0]);
  return letters.join("").toUpperCase() || "?";
}
